import { useState, type ReactNode } from 'react'
import { Accordion } from '@systemeio/components'
import {
  Bell01Icon,
  CalendarSheduledIcon,
  ChevronDownIcon,
  ClockRewindIcon,
  HomeLineIcon,
  PlusIcon,
  PostSheduledIcon,
  Settings02Icon,
  Users01Icon,
} from '@systemeio/icons'
import { twJoin } from 'cn'
import { useTranslation } from 'next-i18next/pages'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useToggle } from 'shared/hooks/use-toggle'
import useUser, { isDomainOwner, isWsAdmin, isWsAssistant } from 'shared/hooks/use-user'
import AddTopic from 'modules/community/components/add-topic'
import { CommunitySearchForm } from 'modules/community/components/search-form'
import { CommunitySearchFormTrigger } from 'modules/community/components/search-form/trigger'
import { TopicList } from 'modules/community/components/topic/components/topic-list'
import { useCommunityWithTopics } from 'modules/community/hooks/use-community-with-topics'
import { hasPermission } from 'modules/community/utils/abac-roles-permissions'

const homeOptionClassName =
  'block w-full cursor-pointer overflow-hidden text-ellipsis px-5 py-1.5 text-left text-text-tertiary hover:text-text-link'

function MobileNav(): ReactNode {
  const { t } = useTranslation()
  const { user } = useUser()
  const { data: community } = useCommunityWithTopics()
  const router = useRouter()
  const { topicPath, communityPath, q } = router.query

  const canViewScheduledPosts = hasPermission(user, 'scheduledPosts', 'schedule', undefined)
  const canViewModeration = hasPermission(user, 'moderation', 'view', null)
  const [topicsValue, setTopicsValue] = useState<string[]>([])
  const isTopicsOpen = topicsValue.includes('topics')
  const [isSearchOpen, toggleSearch] = useToggle(!!q)

  if (!community || !user) {
    return (
      <div className="flex animate-pulse justify-between px-5 py-2 lg:hidden">
        <div className="h-6 w-10/12 rounded-lg bg-bg-tertiary" />
        <div className="h-6 w-1/12 rounded-lg bg-bg-tertiary" />
      </div>
    )
  }

  const isHomePage = router.asPath === `/community/${communityPath}`
  const selectedTopic = community.topics.find(topic => topic.path === topicPath)
  const isSettingsPage = router.pathname.includes('settings')
  const isMembersPage = router.pathname.includes('members')
  const isPendingPostsPage = router.pathname.includes('pending-posts')
  const isScheduledPostsPage = router.pathname.includes('scheduled-posts')
  const isModerationAlertsPage = router.pathname.includes('moderation/alerts')
  const isModerationHistoryPage = router.pathname.includes('moderation/history')

  const menuItems = [
    {
      href: `/community/${communityPath}`,
      icon: <HomeLineIcon className="size-5" />,
      isActive: isHomePage || selectedTopic,
      show: true,
    },
    {
      href: `/community/${communityPath}/members`,
      icon: <Users01Icon className="size-5" />,
      isActive: isMembersPage,
      show: true,
    },
    {
      href: `/community/${communityPath}/moderation/alerts`,
      icon: <Bell01Icon className="size-5" />,
      isActive: isModerationAlertsPage,
      show: canViewModeration,
    },
    {
      href: `/community/${communityPath}/moderation/history`,
      icon: <ClockRewindIcon className="size-5" />,
      isActive: isModerationHistoryPage,
      show: canViewModeration,
    },
    {
      href: `/community/${communityPath}/settings`,
      icon: <Settings02Icon className="size-5" />,
      isActive: isSettingsPage,
      show: isDomainOwner(user) || isWsAdmin(user),
    },
    {
      href: `/community/${communityPath}/pending-posts`,
      icon: <PostSheduledIcon className="size-5" />,
      isActive: isPendingPostsPage,
      show: (isDomainOwner(user) || isWsAdmin(user)) && !community.autoPostsApproval,
    },
    {
      href: `/community/${communityPath}/scheduled-posts`,
      icon: <CalendarSheduledIcon className="size-5" />,
      isActive: isScheduledPostsPage,
      show: canViewScheduledPosts,
    },
  ].filter(item => item.show)

  const topicsPanelKey = selectedTopic?.path ?? 'home'

  return (
    <div className="sticky top-0 z-20 flex w-full flex-col lg:hidden">
      <div className="flex w-full">
        {menuItems.map(({ href, icon, isActive }) => (
          <Link
            href={href}
            key={href}
            className={twJoin(
              `flex flex-1 justify-center border-b-[3px] bg-bg-primary py-3`,
              isActive
                ? 'border-b-border-accent text-text-link'
                : `border-border-100 text-text-secondary`,
            )}
          >
            {icon}
          </Link>
        ))}
      </div>
      <div className="relative flex flex-row items-center gap-x-4 border-b border-border-100 bg-bg-primary px-5 py-2">
        <Accordion.Root
          key={topicsPanelKey}
          value={topicsValue}
          onValueChange={setTopicsValue}
          className="min-w-0 flex-1"
        >
          <Accordion.Item
            value="topics"
            className="overflow-visible rounded-none border-0 bg-transparent"
          >
            <Accordion.Header>
              <Accordion.Trigger
                data-testid="mobile-topics-panel-trigger"
                className="relative flex w-full cursor-default items-center gap-x-1.5 rounded-lg p-0 text-left text-sm font-semibold text-text-primary focus-visible:focus-ring-primary sm:text-sm"
              >
                <span className="line-clamp-2 block overflow-hidden text-ellipsis">
                  {selectedTopic ? selectedTopic.name : t(`mobile_nav.home`)}
                </span>
                <ChevronDownIcon
                  className={twJoin(
                    'size-4 shrink-0 transition-transform duration-200',
                    isTopicsOpen && 'rotate-180',
                  )}
                />
              </Accordion.Trigger>
            </Accordion.Header>
            <Accordion.Panel
              className="absolute top-full left-1/2 z-10 origin-top -translate-x-1/2 transition duration-100 ease-in data-closed:opacity-0"
              style={{ width: '100vw', maxWidth: '100vw' }}
            >
              <Accordion.Content className="p-0">
                <div className="max-h-60 overflow-auto bg-bg-primary py-3 text-base shadow-2 focus:outline-hidden">
                  <button
                    type="button"
                    className={homeOptionClassName}
                    onClick={() => {
                      setTopicsValue([])
                      void router.push(`/community/${communityPath}`)
                    }}
                  >
                    {t(`mobile_nav.home`)}
                  </button>
                  <TopicList topicItemClassName="px-5 py-1.5" />
                </div>
              </Accordion.Content>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion.Root>
        {(isDomainOwner(user) || isWsAdmin(user) || isWsAssistant(user)) && (
          <div className={'flex items-center gap-2 text-text-secondary'}>
            <AddTopic className="text-text-secondary" icon={PlusIcon} />
          </div>
        )}
        <CommunitySearchFormTrigger className="justify-self-end" onOpen={toggleSearch} />
      </div>
      {isSearchOpen && (
        <CommunitySearchForm className="rounded-none border-b border-b-border-100" />
      )}
    </div>
  )
}

export default MobileNav
